import type { Dispatch, SetStateAction } from 'react'
import { Ban, Play, RotateCcw } from 'lucide-react'
import { simulationPresets } from '../defaults'
import { calculateAnalyticalDesign, validateThickGeometry } from '../physics'
import type { AppState, SimulationPreset, SimulationResult } from '../types'
import { HorizontalScrollRegion } from './HorizontalScrollRegion'
import { IntensityPlot, RayPlot } from './Plots'
import { EmptyState, Metric, ModelBadge, Notice, NumberField, Section, SelectField, formatNumber } from './ui'

interface RaySimulationTabProps {
  state: AppState
  setState: Dispatch<SetStateAction<AppState>>
  result: SimulationResult | null
  stale: boolean
  runState: {
    kind: 'simulation' | 'monte-carlo' | null
    running: boolean
    progress: number
    message: string
    error: string | null
  }
  onRun: () => void
  onCancel: () => void
}

export function RaySimulationTab({
  state,
  setState,
  result,
  stale,
  runState,
  onRun,
  onCancel,
}: RaySimulationTabProps) {
  const design = calculateAnalyticalDesign(state)
  const thickGeometry = validateThickGeometry(
    state.lens.thick.pitchMm,
    state.lens.thick.radiusMm,
    state.lens.thick.centerThicknessMm,
  )
  const isRunning = runState.running && runState.kind === 'simulation'
  const finiteCrosstalk = result?.crosstalk.filter(Number.isFinite) ?? []
  const finiteDesired = result?.desiredTransmission.filter(Number.isFinite) ?? []
  const finiteErrors = result?.zoneCenterErrorsMm.filter(Number.isFinite).map(Math.abs) ?? []
  const worstCrosstalk = finiteCrosstalk.length ? Math.max(...finiteCrosstalk) : null
  const minimumDesired = finiteDesired.length ? Math.min(...finiteDesired) : null
  const worstZoneError = finiteErrors.length ? Math.max(...finiteErrors) : null

  const updateSettings = (patch: Partial<AppState['simulationSettings']>) =>
    setState((current) => ({
      ...current,
      simulationSettings: { ...current.simulationSettings, ...patch },
    }))

  const validationMessages = [...design.missing]
  if (state.lens.mode === 'thin') {
    if (!state.lens.thin.useDesignTarget && !state.lens.thin.pitchMm) validationMessages.push('actual lens pitch')
    if (!state.lens.thin.useDesignTarget && !state.lens.thin.focalLengthMm) validationMessages.push('focal length')
    if (!state.lens.thin.useDesignTarget && state.lens.thin.focalReference === 'vendor-unspecified') validationMessages.push('unambiguous focal reference')
  } else {
    validationMessages.push(...thickGeometry.errors)
    if (!state.lens.thick.refractiveIndex) validationMessages.push('lens refractive index')
  }

  return (
    <div className="tab-stack">
      <div className="result-header">
        <div>
          <ModelBadge>{state.lens.mode === 'thin' ? 'paraxial thin-lens simulation' : 'geometric thick-lens ray trace'}</ModelBadge>
          <h1>Ray simulation</h1>
          <p>Weighted rays generate both the plotted paths and the numerical viewer-plane intensity.</p>
        </div>
        <div className="run-controls">
          {isRunning ? (
            <button className="danger-button" onClick={onCancel}><Ban size={16} /> Cancel run</button>
          ) : (
            <button className="primary-button" onClick={onRun} disabled={validationMessages.length > 0}><Play size={16} /> Run {state.lens.mode === 'thin' ? 'paraxial' : 'geometric'} trace</button>
          )}
        </div>
      </div>

      {validationMessages.length ? <Notice tone="warning" title="Simulation inputs incomplete">{[...new Set(validationMessages)].join(', ')}.</Notice> : null}
      {runState.error && runState.kind === 'simulation' ? <Notice tone="danger" title="Simulation failed">{runState.error}</Notice> : null}
      {isRunning ? (
        <div className="progress-panel" role="status" aria-live="polite">
          <div className="progress-label"><strong>{runState.message}</strong><span>{Math.round(runState.progress * 100)}%</span></div>
          <progress value={runState.progress} max={1} />
          <span>Runs in a Web Worker. Cancellation occurs after the current deterministic chunk.</span>
        </div>
      ) : null}

      <Section title="Sampling controls" description="Presets change deterministic stratified sample counts; no random sampling is used in the ray trace.">
        <div className="form-grid four">
          <SelectField
            label="Performance preset"
            value={state.simulationSettings.preset}
            onChange={(value) => setState((current) => ({ ...current, simulationSettings: { ...simulationPresets[value as SimulationPreset] } }))}
            options={[
              { value: 'preview', label: 'Preview' },
              { value: 'balanced', label: 'Balanced' },
              { value: 'high', label: 'High precision' },
            ]}
          />
          <NumberField label="Source samples" value={state.simulationSettings.sourceSamples} onChange={(value) => updateSettings({ sourceSamples: Math.round(Math.max(1, Math.min(31, value ?? 1))) })} min={1} max={31} step={1} />
          <NumberField label="Aperture samples" value={state.simulationSettings.apertureSamples} onChange={(value) => updateSettings({ apertureSamples: Math.round(Math.max(3, Math.min(101, value ?? 3))) })} min={3} max={101} step={1} />
          <NumberField label="Sampled lens repeats" value={state.simulationSettings.lenslets} onChange={(value) => updateSettings({ lenslets: Math.round(Math.max(5, Math.min(61, value ?? 5))) })} min={5} max={61} step={1} />
        </div>
        <details className="advanced-panel">
          <summary>Viewer grid</summary>
          <div className="form-grid two">
            <NumberField label="Viewer bins" value={state.simulationSettings.viewerBins} onChange={(value) => updateSettings({ viewerBins: Math.round(Math.max(80, Math.min(1600, value ?? 80))) })} min={80} max={1600} step={1} />
            <NumberField label="Viewer span" unit="mm" value={state.simulationSettings.viewerSpanMm} onChange={(value) => updateSettings({ viewerSpanMm: Math.max(10, Math.min(2000, value ?? 10)) })} min={10} max={2000} />
          </div>
        </details>
      </Section>

      {!result ? (
        <EmptyState title="No ray result yet">Complete the active lens specification and run a trace. Numerical results and the ray drawing will come from the same worker calculation.</EmptyState>
      ) : (
        <>
          {stale ? <Notice tone="warning" title="Result is stale">An input changed after this run. The old result remains visible for comparison but does not describe the current setup. <button className="text-button" onClick={onRun}><RotateCcw size={13} /> Re-run</button></Notice> : null}
          <Section title="Viewer-plane result" description="Relative throughput uses launched weighted ray energy as the denominator. It is not an absolute photometric transmission.">
            <div className="section-model-row"><ModelBadge>{result.model}</ModelBadge><span>{result.settings.preset} preset, {result.settings.sourceSamples} × {result.settings.apertureSamples} samples, {result.settings.lenslets} repeats</span></div>
            <div className="metric-grid">
              <Metric label="Worst crosstalk" value={formatNumber(worstCrosstalk !== null ? worstCrosstalk * 100 : null, 2)} unit="%" />
              <Metric label="Minimum desired-view transmission" value={formatNumber(minimumDesired !== null ? minimumDesired * 100 : null, 2)} unit="% launched" />
              <Metric label="Worst zone-center error" value={formatNumber(worstZoneError, 3)} unit="mm" />
              <Metric label="Total relative throughput" value={formatNumber(result.relativeThroughput * 100, 2)} unit="% launched" />
              <Metric label="TIR rays" value={result.discarded.tir.toLocaleString()} />
              <Metric label="Unreachable rays" value={result.discarded.unreachable.toLocaleString()} />
              <Metric label="Missed / clipped surface" value={result.discarded.missedSurface.toLocaleString()} />
              <Metric label="Worker time" value={formatNumber(result.elapsedMs, 0)} unit="ms" />
            </div>
            {result.warnings.map((warning) => <Notice key={warning} tone="warning">{warning}</Notice>)}
            <IntensityPlot series={result.intensity} targets={result.targetCentersMm} />
          </Section>

          <Section title="Pupil transfer matrix H(j,k)" description="Rows are target pupils; columns are physical source-view indices. Desired source assignment accounts for image inversion.">
            <HorizontalScrollRegion className="table-scroll" label="Pupil transfer matrix">
              <table className="data-table matrix-table">
                <thead><tr><th>Target</th>{result.intensity.map((item) => <th key={item.view}>Source {item.view}</th>)}<th>Desired source</th><th>Crosstalk</th><th>Desired transmission</th></tr></thead>
                <tbody>
                  {result.transferMatrix.map((row, targetIndex) => (
                    <tr key={targetIndex}>
                      <th>T{targetIndex + 1} at {formatNumber(result.targetCentersMm[targetIndex], 1)} mm</th>
                      {row.map((value, sourceIndex) => <td key={sourceIndex} className={sourceIndex === result.desiredSourceByTarget[targetIndex] ? 'desired-cell' : ''}>{formatNumber(value, 5)}</td>)}
                      <td>Source {result.desiredSourceByTarget[targetIndex]}</td>
                      <td>{formatNumber(result.crosstalk[targetIndex] * 100, 2)}%</td>
                      <td>{formatNumber(result.desiredTransmission[targetIndex] * 100, 2)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </HorizontalScrollRegion>
          </Section>

          <Section title="Ray paths" description="Horizontal positions are exact within the active model; the diagram uses separate horizontal and axial scales so lens-plane behavior remains visible.">
            <RayPlot rays={result.rays} />
          </Section>

          {result.characterization ? (
            <Section title="Numerical thick-lens characterization" description="Near-axis focal estimates and full-trace angular/throughput quantities retain distinct reference conventions.">
              <ModelBadge>geometric thick-lens ray trace</ModelBadge>
              <div className="metric-grid compact-top">
                <Metric label="Near-axis effective focal behavior" value={formatNumber(result.characterization.effectiveFocalLengthMm, 6)} unit="mm" />
                <Metric label="BFD from front vertex" value={formatNumber(result.characterization.paraxialBackFocalDistanceMm, 6)} unit="mm" />
                <Metric label="Principal plane from rear" value={formatNumber(result.characterization.principalPlaneFromRearMm, 6)} unit="mm" />
                <Metric label="Output angular σ" value={formatNumber(result.characterization.outputAngularSpreadDeg, 4)} unit="deg" />
                <Metric label="TIR fraction" value={formatNumber(result.characterization.tirFraction * 100, 3)} unit="%" />
              </div>
              {Object.keys(result.characterization.chromaticShiftMm).length ? (
                <HorizontalScrollRegion className="table-scroll compact-top" label="Chromatic centroid shifts">
                  <table className="data-table"><thead><tr><th>Wavelength / index entry</th><th>Zone centroid shift relative to green</th></tr></thead><tbody>{Object.entries(result.characterization.chromaticShiftMm).map(([label, shift]) => <tr key={label}><td>{label}</td><td>{formatNumber(shift, 5)} mm</td></tr>)}</tbody></table>
                </HorizontalScrollRegion>
              ) : null}
            </Section>
          ) : null}
        </>
      )}
    </div>
  )
}
